import { compileStudioProfile, type CompiledStudioItemMaster } from "./itemMasterWorkbook";
import {
  normalizeCdtStudioCategoryKey,
  type JsonValue,
  type PayloadPutCdtStudioConfiguration,
} from "@CommonApi/merchant-settings";
import { migrateStudioProfile, validateStudioProfile, type StudioProfile } from "./studioProfile";
import { walmartStudioProfile } from "./walmartItemMaster";

export interface StudioCategoryConfiguration {
  profile: StudioProfile;
  profileJson?: string;
}

/** Category memory is merchant-scoped; category codes are not globally unique. */
export interface StudioCategoryIdentity {
  merchantId: number;
  merchantName: string;
  categoryGroup: string;
  categoryCode: string;
  categoryStatus?: "ACTIVE" | "ARCHIVED";
}

interface StoredCategoryConfiguration {
  active?: StudioCategoryConfiguration;
  draft?: StudioCategoryConfiguration;
}

export interface StudioCategoryConfigurationDebugInfo {
  inMemoryConfiguration: "missing" | "valid";
}

export class StudioConfigurationSaveError extends Error {
  constructor() {
    super("Could not save the CDT Studio configuration.");
    this.name = "StudioConfigurationSaveError";
  }
}

const CONFIGURATION_BY_CATEGORY = new Map<string, StoredCategoryConfiguration>();

const clone = <T>(value: T): T => structuredClone(value);

const jsonValueOf = (value: unknown): JsonValue => {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (Array.isArray(value)) return value.map(jsonValueOf);
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, jsonValueOf(entry)]),
    );
  }
  throw new Error("CDT Studio configuration contains a non-JSON value");
};

const categoryKey = ({ merchantId, categoryGroup, categoryCode }: StudioCategoryIdentity): string =>
  [String(merchantId), categoryGroup, categoryCode]
    .map(normalizeCdtStudioCategoryKey)
    .join("\u0000");

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isConfigurationCandidate = (value: unknown): value is StudioCategoryConfiguration => {
  if (!isRecord(value) || !isRecord(value.profile)) return false;
  return value.profileJson === undefined || typeof value.profileJson === "string";
};

/** A saved or pasted profile brought up to the current schema; throws when it is not a valid one. */
export const parseStudioProfile = (value: unknown): StudioProfile => {
  // Validation below is what proves the migrated value is a profile.
  const profile = migrateStudioProfile(
    value,
    walmartStudioProfile.aestheticsRules,
  ) as StudioProfile;
  validateStudioProfile(profile);
  return profile;
};

export const studioCategoryConfigurationFromUnknown = (
  value: unknown,
): StudioCategoryConfiguration | undefined => {
  if (!isConfigurationCandidate(value)) return undefined;
  try {
    const profile = parseStudioProfile(value.profile);
    // A migrated profile's saved JSON still spells the old schema.
    const profileJson =
      profile === value.profile ? value.profileJson : JSON.stringify(profile, null, 2);
    return clone({ profile, profileJson });
  } catch {
    return undefined;
  }
};

const serverConfigurationFor = (
  identity: StudioCategoryIdentity,
  serverConfigurations?: Record<string, unknown>,
): StudioCategoryConfiguration | undefined => {
  const normalizedCode = normalizeCdtStudioCategoryKey(identity.categoryCode);
  const serverConfiguration = Object.entries(serverConfigurations ?? {}).find(
    ([key]) => normalizeCdtStudioCategoryKey(key) === normalizedCode,
  )?.[1];
  return studioCategoryConfigurationFromUnknown(serverConfiguration);
};

const isWalmart = (merchantName: string): boolean => /walmart/i.test(merchantName);

export const neutralStudioProfile = (merchantName: string): StudioProfile => {
  const walmart = clone(walmartStudioProfile);
  const profile: StudioProfile = {
    ...walmart,
    merchantName: merchantName.trim() || "Merchant",
    /* The same bridges as Walmart's: the live `variant` IS the subcategory (CDT 0, the root
       the studio asserts), so the Subcategory caption names it, and Flavour names the live
       `flavour`. Captioning `variant` "Flavour" put the subcategories under a Flavour level,
       with a second Subcategory level beside it wherever the item master also has a
       `section` column. */
    attributes: [
      {
        key: "section",
        label: "Subcategory",
        column: "CDT 0",
        pogField: "subCategory",
        overlayAttr: "variant",
      },
      { key: "brand", label: "Brand", column: "CDT 1", pogField: "brand" },
      { key: "packType", label: "Pack type", column: "CDT 2", pogField: "cdt2" },
      {
        key: "variant",
        label: "Flavour",
        column: "CDT 3",
        pogField: "variant",
        overlayAttr: "flavour",
      },
    ],
    semanticRoles: {
      blockIdentity: ["section", "brand"],
      vertical: ["section", "brand"],
      brand: "brand",
      tier: "packType",
      quantity: "variant",
      terminalVariant: "variant",
    },
    firstValueRules: [],
    lastValueRules: [],
    preferredValueRules: { first: false, last: false },
    quantityNormalization: {
      ...walmart.quantityNormalization,
      enabled: false,
      reconciliationBlockAttributes: false,
    },
    aestheticsRules: {
      ...walmart.aestheticsRules,
      brandAttr: "brand",
      tierAttr: "packType",
      sizeAttr: "variant",
      flavourAttr: "variant",
      verticalAttrs: ["section", "brand"],
      tierOrder: [],
      lastInBlock: [],
      bayOwners: [],
    },
  };
  delete profile.sectionSemantics;
  return profile;
};

const defaultsFor = (identity: StudioCategoryIdentity): StudioCategoryConfiguration => {
  const profile = isWalmart(identity.merchantName)
    ? clone(walmartStudioProfile)
    : neutralStudioProfile(identity.merchantName);
  const normalizedCode = identity.categoryCode.trim();
  if (normalizedCode) profile.categoryName = normalizedCode;
  return {
    profile,
    profileJson: JSON.stringify(profile, null, 2),
  };
};

export const getStudioCategoryConfigurationDebugInfo = (
  identity: StudioCategoryIdentity,
): StudioCategoryConfigurationDebugInfo => ({
  inMemoryConfiguration: CONFIGURATION_BY_CATEGORY.get(categoryKey(identity))?.active
    ? "valid"
    : "missing",
});

const storedFor = (identity: StudioCategoryIdentity): StoredCategoryConfiguration => {
  const key = categoryKey(identity);
  const current = CONFIGURATION_BY_CATEGORY.get(key);
  if (current) return current;
  const stored: StoredCategoryConfiguration = {};
  CONFIGURATION_BY_CATEGORY.set(key, stored);
  return stored;
};

/** Merchant-settings copy only. Session drafts and bundled fixtures never open the tab. */
export const getStudioCategoryConfiguration = (
  identity: StudioCategoryIdentity,
  serverConfigurations?: Record<string, unknown>,
): StudioCategoryConfiguration | undefined =>
  serverConfigurationFor(identity, serverConfigurations);

/** Session copy after Save in this page; not a substitute for merchant settings. */
export const getSessionStudioCategoryConfiguration = (
  identity: StudioCategoryIdentity,
): StudioCategoryConfiguration | undefined => {
  const active = storedFor(identity).active;
  return active ? clone(active) : undefined;
};

export const getStudioCategoryDraft = (
  identity: StudioCategoryIdentity,
): StudioCategoryConfiguration => {
  const stored = storedFor(identity);
  return clone(stored.draft ?? stored.active ?? defaultsFor(identity));
};

export const payloadForStudioCategoryConfiguration = (
  identity: StudioCategoryIdentity,
  configuration: StudioCategoryConfiguration,
): PayloadPutCdtStudioConfiguration => ({
  categoryCode: identity.categoryCode,
  configuration: {
    profile: jsonValueOf(configuration.profile),
    profileJson: configuration.profileJson,
  },
});

export const saveStudioCategoryDraft = (
  identity: StudioCategoryIdentity,
  draft: StudioCategoryConfiguration,
): void => {
  const key = categoryKey(identity);
  const stored = storedFor(identity);
  CONFIGURATION_BY_CATEGORY.set(key, { ...stored, draft: clone(draft) });
};

export const hydrateStudioCategoryConfiguration = (
  identity: StudioCategoryIdentity,
  configuration: StudioCategoryConfiguration,
): void => {
  const active = clone(configuration);
  validateStudioProfile(active.profile);
  CONFIGURATION_BY_CATEGORY.set(categoryKey(identity), {
    active,
    draft: clone(active),
  });
};

export const activateStudioCategoryConfiguration = (
  identity: StudioCategoryIdentity,
  configuration: StudioCategoryConfiguration,
): CompiledStudioItemMaster => {
  if (identity.categoryStatus === "ARCHIVED") {
    throw new Error(
      `Cannot activate CDT Studio configuration for archived category ${identity.categoryCode}`,
    );
  }
  const saved = clone({
    ...configuration,
    profile: { ...configuration.profile, categoryName: identity.categoryCode.trim() },
  });
  const compiled = compileStudioProfile(saved.profile);
  CONFIGURATION_BY_CATEGORY.set(categoryKey(identity), {
    active: saved,
    draft: clone(saved),
  });
  return compiled;
};
